/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Slobodan Djordjevic
 */

import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { UpgradeDialog, useToken } from "trux"
import { useHistory } from "react-router-dom"

import "./UpgradeDialogComponent.css"
import {
    useFindProfileQuery,
    useAddStripeCheckoutSessionMutation,
} from "../../generated/graphql"

// Vars

const MONTHLY_PRICE_ID =
    process.env.REACT_APP_STRIPE_TRACE_ID_PLUS_PRICE_ID_MONTHLY
const YEARLY_PRICE_ID =
    process.env.REACT_APP_STRIPE_TRACE_ID_PLUS_PRICE_ID_YEARLY

// Upgrade Dialog Component

const UpgradeDialogComponent = () => {
    // get history
    const history = useHistory()

    // get dispatch
    const dispatch = useDispatch()

    // load token
    const token = useToken("UpgradeDialogComponent")
    const tokenReady = token !== null

    // get the user_id from redux store
    const loggedInUser = useSelector((state) => state.user.value)

    // query for profile
    const {
        data: profileData,
        loading: profileLoading,
        error: profileError,
    } = useFindProfileQuery({
        variables: {
            user_id: loggedInUser?.user_id,
            token: token,
        },
        skip: !tokenReady,
    })

    // grab profile from query result
    let profile = profileData && profileData.profile ? profileData.profile : {}

    // get the full url
    let fullURL = window.location.href
    if (!fullURL.endsWith("/")) fullURL += "/"

    // Create success and canel URLs
    let successURL = `${fullURL}success?session_id={CHECKOUT_SESSION_ID}&user_id=${profile.user_id}`
    const cancelURL = `${fullURL}cancel`

    // Add stripe checkout session mutationss
    const [addStripeCheckoutSessionMutation, { data, loading, error }] =
        useAddStripeCheckoutSessionMutation({
            variables: {
                token: token,
                profile_hash_key: profile.hash_key,
                success_url: successURL,
                cancel_url: cancelURL,
            },
        })

    const toastIt = (msg) => {
        dispatch({
            type: "COMMON_SET",
            key: "toast",
            value: msg,
        })
    }

    const handleOnUpgradeClick = (e, interval) => {
        history.replace(".")
        toastIt("Redirecting now...")
        const monthly = interval === "monthly"
        const priceID =
            interval === "monthly" ? MONTHLY_PRICE_ID : YEARLY_PRICE_ID
        const successSuffix = monthly ? "month" : "year"
        successURL = successURL.replace("success", `success/${successSuffix}`)
        // Add the collection
        let mutationPromise = addStripeCheckoutSessionMutation({
            variables: {
                token: token,
                price_id: priceID,
                profile_hash_key: profile.hash_key,
                success_url: successURL,
                cancel_url: cancelURL,
            },
        })
        mutationPromise
            .then((res) => {
                window.location.href =
                    res.data.addStripeCheckoutSession.session_url
            })
            .catch((err) => {
                console.log(err)
                toastIt("Something went wrong")
            })
    }

    const handleUpgradeDialogOnClose = () => {
        history.replace("..")
    }

    let upgradeeFullName = loggedInUser
        ? loggedInUser.last_name
            ? `${loggedInUser.first_name} ${loggedInUser.last_name}`
            : loggedInUser.first_name
        : null
    let upgradeeContact =
        loggedInUser && loggedInUser.email ? loggedInUser.email : null

    return (
        <UpgradeDialog
            upgradeeFullName={upgradeeFullName}
            upgradeeContact={upgradeeContact}
            onClose={handleUpgradeDialogOnClose}
            onUpgradeClick={handleOnUpgradeClick}
        />
    )
}

export { UpgradeDialogComponent }
